import type { ReactNode } from 'react';

import { cn } from '../../../shared/lib/cn';
import { Button } from '../../../shared/ui/button';
import { Checkbox } from '../../../shared/ui/checkbox';
import { FieldLabel } from '../../../shared/ui/field-label';
import { Hint } from '../../../shared/ui/hint';
import { Input } from '../../../shared/ui/input';
import { Card } from '../../../shared/ui/layout';
import { OptionCard } from '../../../shared/ui/option-card';
import { Select } from '../../../shared/ui/select';
import type { TAlgorithm, TSignatureEncoding } from '../../../entities/auth-preset';
import { signsTime, skewSeconds, type IAnswers, type TSignedShape, type TTimePlace } from '../lib';

const SHAPES: { value: TSignedShape; title: string; beside: string }[] = [
  { value: 'body', title: 'Just the body', beside: 'GitHub, Shopify' },
  { value: 'time-dot-body', title: 'Time, a dot, then the body', beside: 'Stripe, Knock' },
  { value: 'method-path-body', title: 'Method, path, body', beside: 'rare' },
];

const TIME_PLACES: { value: TTimePlace; label: string; description: string }[] = [
  {
    value: 'same_header',
    label: 'the same header, key t',
    description: 'the signature header is read as fields — t for the time, v1 for the signature',
  },
  {
    value: 'header',
    label: 'a header of its own',
    description: 'the signature header holds the signature and nothing else',
  },
];

const Bare = ({ children }: { children: ReactNode; className?: string }) => <>{children}</>;

const Radio = ({ on }: { on: boolean }) => (
  <span
    className={cn(
      'flex size-check items-center justify-center rounded-control border',
      on ? 'border-accent' : 'border-suppressed',
    )}
  >
    {on && <span className="block size-2 rounded-control bg-accent" />}
  </span>
);

interface IByHandFormProps {
  answers: IAnswers;
  onChange: (next: IAnswers) => void;
  onUse?: () => void;
  bare?: boolean;
  secret?: string;
  onSecretChange?: (next: string) => void;
}

export const ByHandForm = ({
  answers,
  onChange,
  onUse,
  bare,
  secret,
  onSecretChange,
}: IByHandFormProps) => {
  const set = (patch: Partial<IAnswers>) => onChange({ ...answers, ...patch });

  const Frame = bare ? Bare : Card;
  const timed = signsTime(answers);
  const sameHeader = timed && answers.timePlace === 'same_header';
  const skewBad = skewSeconds(answers.skewSec) === null;

  return (
    <Frame className={bare ? undefined : 'p-0'}>
      {!bare && (
        <div className="px-4 pt-4">
          <h2 className="text-title font-semibold tracking-title text-primary">
            Set it up by hand
          </h2>
          <p className="mt-2 text-data text-muted">
            For a sender that is not in the catalogue but signs the ordinary way.
          </p>
        </div>
      )}

      <div className="flex flex-col gap-4 px-4 py-4">
        <div>
          <FieldLabel htmlFor="q-name">Name it</FieldLabel>
          <Input
            id="q-name"
            placeholder="Vendor portal"
            value={answers.name}
            onChange={(event) => set({ name: event.target.value })}
          />
        </div>

        <div className="grid grid-cols-2 gap-x-5 gap-y-4">
          <div>
            <FieldLabel htmlFor="q-algorithm">Algorithm</FieldLabel>
            <Select
              id="q-algorithm"
              value={answers.algorithm}
              onChange={(algorithm: TAlgorithm) => set({ algorithm })}
              options={[
                { value: 'sha256', label: 'sha256' },
                { value: 'sha1', label: 'sha1' },
                { value: 'sha512', label: 'sha512' },
              ]}
            />
          </div>
          <div>
            <FieldLabel htmlFor="q-encoding">Encoding</FieldLabel>
            <Select
              id="q-encoding"
              value={answers.encoding}
              onChange={(encoding: TSignatureEncoding) => set({ encoding })}
              options={[
                { value: 'hex', label: 'hex' },
                { value: 'base64', label: 'base64' },
                { value: 'base64url', label: 'base64url' },
              ]}
            />
          </div>
        </div>

        <div>
          <FieldLabel htmlFor="q-header">Header carrying the signature</FieldLabel>
          <Input
            id="q-header"
            mono
            placeholder="X-Vp-Sign"
            value={answers.header}
            onChange={(event) => set({ header: event.target.value })}
          />
        </div>

        {!sameHeader && (
          <div>
            <FieldLabel htmlFor="q-prefix">Prefix in the value, if any</FieldLabel>
            <Input
              id="q-prefix"
              mono
              placeholder="sha256="
              value={answers.prefix}
              onChange={(event) => set({ prefix: event.target.value })}
            />
          </div>
        )}

        <div>
          <FieldLabel>What gets signed</FieldLabel>
          <div className="flex flex-col gap-2">
            {SHAPES.map((shape) => (
              <OptionCard
                key={shape.value}
                as="label"
                selected={answers.shape === shape.value}
                control={
                  <>
                    <input
                      type="radio"
                      name="signed-shape"
                      className="sr-only"
                      checked={answers.shape === shape.value}
                      onChange={() => set({ shape: shape.value })}
                    />
                    <Radio on={answers.shape === shape.value} />
                  </>
                }
                title={
                  <span className="flex items-center gap-inset">
                    <span className="grow">{shape.title}</span>
                    <span className="font-mono text-micro font-normal text-faint">
                      {shape.beside}
                    </span>
                  </span>
                }
              />
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-x-5 gap-y-4">
          <div>
            <FieldLabel htmlFor="q-time-place">Time is in the header</FieldLabel>
            <Select
              id="q-time-place"
              disabled={!timed}
              value={answers.timePlace}
              onChange={(timePlace: TTimePlace) => set({ timePlace })}
              options={TIME_PLACES}
            />
          </div>
          <div>
            <FieldLabel htmlFor="q-skew">Accept a skew of</FieldLabel>
            <Input
              id="q-skew"
              mono
              inputMode="numeric"
              disabled={!timed}
              invalid={timed && skewBad}
              value={answers.skewSec}
              onChange={(event) => set({ skewSec: event.target.value })}
              trailing={<span className="font-mono text-micro text-faint">seconds</span>}
            />
          </div>
        </div>

        {timed && answers.timePlace === 'header' && (
          <div>
            <FieldLabel htmlFor="q-time-header">Header carrying the timestamp</FieldLabel>
            <Input
              id="q-time-header"
              mono
              placeholder="X-Vp-Timestamp"
              value={answers.timeHeader}
              onChange={(event) => set({ timeHeader: event.target.value })}
            />
          </div>
        )}

        {/* This checkbox writes `shape`, not a flag: a signed string with no time in it IS the opt-out. */}
        <OptionCard
          selected={!timed}
          control={
            <Checkbox
              aria-label="This sender does not sign time"
              checked={!timed}
              onCheckedChange={(next) => set({ shape: next ? 'body' : 'time-dot-body' })}
            />
          }
          title={
            <span className="flex items-center gap-inset">
              <span className="grow">This sender does not sign time</span>
              <span className="font-mono text-micro font-normal text-faint">
                no replay protection
              </span>
            </span>
          }
        />

        {onSecretChange && (
          <div>
            <FieldLabel htmlFor="q-secret">Secret</FieldLabel>
            <Input
              id="q-secret"
              mono
              type="password"
              autoComplete="off"
              placeholder="paste it from their dashboard"
              value={secret ?? ''}
              onChange={(event) => onSecretChange(event.target.value)}
            />
            <Hint>Saved on this source only</Hint>
          </div>
        )}
      </div>

      {!bare && (
        <div className="flex items-center gap-inset bg-field-muted px-5 py-3">
          <Button size="sm" className="ml-auto" onClick={onUse}>
            Use these answers
          </Button>
        </div>
      )}
    </Frame>
  );
};
